import { Select, Typography } from "antd";
import { useEffect, useState } from "react";
import { subscription, Subscription } from "../utils/http";
import { InputObject } from "./default";
import { emptyOption } from "./ComboBox.default";

export interface ComboBoxProps {
  readonly className?: string;
  readonly readOnly?: boolean;
  readonly placeholder?: string;
  readonly value?: InputObject;
  readonly request: (search: string) => Promise<InputObject[]>;
  readonly onChange?: (value?: InputObject) => void;
  readonly disabled?: boolean;
}

function ComboBox(props: ComboBoxProps) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<
    {
      readonly value?: any;
      readonly label: string;
      readonly options?: {
        readonly value?: any;
        readonly label: string;
      }[];
    }[]
  >([emptyOption]);
  const [subscribe, setSubscribe] = useState<Subscription | null>(null);

  // Events

  function onSearch(value: string): void {
    loadData(value);
  }

  function onDropdownVisibleChange(open: boolean): void {
    if (open) {
      loadData("");
    }
  }

  function onChange(_: any, option: any): void {
    const input: InputObject = {
      display: option.label,
      subDisplay: option.subLabel,
      value: option.value,
      dataItem: option.dataItem,
    };

    props.onChange?.(input);
  }

  function loadData(search: string): void {
    setOptions([emptyOption]);
    setLoading(true);
    subscribe?.unsubscribe();
    const $$ = subscription(() => props.request(search), 250);
    setSubscribe($$);
    $$.then((data) => {
      const options = [
        emptyOption,
        ...(data || []).map((item) => {
          if (Array.isArray(item.value)) {
            return {
              label: item.display,
              options: item.value.map((item) => ({
                label: item.display,
                subLabel: item.subDisplay,
                value: item.value,
                dataItem: item.dataItem,
              })),
            };
          }

          return {
            label: item.display,
            subLabel: item.subDisplay,
            value: item.value,
            dataItem: item.dataItem,
          };
        }),
      ];

      setOptions(options);
      setLoading(false);
    }).catch(() => {
      setOptions([emptyOption]);
      setLoading(false);
    });
  }

  return (
    <Select
      className={props.className}
      disabled={props.disabled}
      loading={loading}
      style={props.readOnly ? { pointerEvents: "none" } : {}}
      value={
        props.value
          ? {
              label: props.value.display,
              subLabel: props.value.subDisplay,
              value: props.value.value,
              dataItem: props.value.dataItem,
            }
          : undefined
      }
      placeholder={props.placeholder}
      options={options}
      defaultActiveFirstOption={false}
      filterOption={false}
      notFoundContent={null}
      optionRender={(option) => (
        <Typography.Text className="inline-flex flex-col">
          <Typography.Text>{option.label}</Typography.Text>
          <Typography.Text type="secondary">
            {option.data.subLabel}
          </Typography.Text>
        </Typography.Text>
      )}
      showSearch
      onChange={onChange}
      onSearch={onSearch}
      onDropdownVisibleChange={onDropdownVisibleChange}
    />
  );
}

export default ComboBox;
